import React, { useState } from 'react'
import type { DisplaySummaryRow } from '../../../utils/projectOverviewSummary'
import type {
  OverviewHoursPieSlice,
  OverviewTopProjectBarRow,
  UtilDistributionBand,
} from '../../../utils/projectOverviewCharts'
import { OverviewHoursMixPieChart } from './charts/overview-hours-mix-pie-chart'
import { OverviewTopProjectsBarChart } from './charts/overview-top-projects-bar-chart'
import { OverviewUtilDistributionBarChart } from './charts/overview-util-distribution-bar-chart'

export interface ProjectOverviewChartsSectionProps {
  isDark: boolean
  displaySummary: DisplaySummaryRow | null
  overviewHoursPieData: OverviewHoursPieSlice[]
  overviewTopProjectsBarData: OverviewTopProjectBarRow[]
  overviewUtilDistribution: UtilDistributionBand[]
}

/** Collapsible chrome uses same white / slate border stack as `ChartsSection` cards. */
export function ProjectOverviewChartsSection({
  isDark,
  displaySummary,
  overviewHoursPieData,
  overviewTopProjectsBarData,
  overviewUtilDistribution,
}: ProjectOverviewChartsSectionProps) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="rounded-xl shadow-md border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left bg-slate-50 border-b border-slate-200 hover:bg-slate-100 transition-colors"
        aria-expanded={expanded}
      >
        <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
          Charts & visuals
        </span>
        <span
          className={[
            'flex items-center justify-center w-7 h-7 rounded-lg border text-sm font-semibold',
            expanded
              ? 'border-slate-300 bg-white text-slate-700'
              : 'border-slate-200 bg-white text-slate-500',
          ].join(' ')}
          aria-hidden
        >
          {expanded ? '−' : '+'}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ease-in-out overflow-hidden ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        aria-hidden={!expanded}
      >
        <div className="min-h-0">
          {displaySummary && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 py-4">
              <OverviewHoursMixPieChart
                isDark={isDark}
                displaySummary={displaySummary}
                overviewHoursPieData={overviewHoursPieData}
              />
              <OverviewTopProjectsBarChart
                isDark={isDark}
                data={overviewTopProjectsBarData}
              />
              <OverviewUtilDistributionBarChart
                isDark={isDark}
                data={overviewUtilDistribution}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
